import clsx from 'clsx';
import { forwardRef } from 'react';
import IconPlus from '@/components/IconPlus';
import { useWhitelistedChannels as useSafelistedChannels } from '@/components/UBSafelist/hooks/useWhitelistedChannels';
import ChannelImage from '@/components/YouTubeChannelImage';
import { useHasPermission, useSubscriptionType } from '@/hooks/auth';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { useSettings } from '@/hooks/state/useSettings';
import { getMaxSafelistedChannels } from './getMaxSafelistedChannels';
import { SafelistedChannelsIcon } from './SafelistedChannelsIcon';
import { cn } from '@/lib/utils';
import { Ellipsis } from '@music-vine/cadence/icons';
import { Button } from '@music-vine/cadence/ui';

// The top nav only ever shows three channel avatars, so Pro/Business users
// (who can safelist more) get an ellipsis instead of the add icon once they
// reach this many safelisted channels.
const DISPLAYED_CHANNELS_LIMIT = 3;
const SafelistedChannels = forwardRef<HTMLButtonElement>((props, ref) => {
  const {
    data: safelistedChannels
  } = useSafelistedChannels();
  const accountType = useSubscriptionType();
  const {
    overduePayment
  } = useOverduePayment();
  const {
    id: overduePaymentId
  } = overduePayment ?? {};
  const {
    settings
  } = useSettings();
  const isPaused = !!settings?.subscriptionData?.isPaused;
  const safelistedChannelCount = safelistedChannels?.length ?? 0;
  // One source of truth for the per-plan limit — covers Business too.
  const safelistedChannelLimit = accountType === 'Free' ? 0 : getMaxSafelistedChannels({
    subscriptionType: accountType
  });
  const canSafelistMoreChanels = safelistedChannelCount < safelistedChannelLimit;
  const isProOrBusiness = accountType === 'Pro' || accountType === 'Business';
  const hasReachedDisplayedLimit = safelistedChannelCount >= DISPLAYED_CHANNELS_LIMIT;
  const showAddChannelIcon = canSafelistMoreChanels && !overduePaymentId && !(isProOrBusiness && hasReachedDisplayedLimit);
  const showEllipsis = isProOrBusiness && hasReachedDisplayedLimit;
  const hasSafelistPermission = useHasPermission('safelist_yt');
  return <Button ref={ref} type="button" variant="light" {...props} className={clsx('relative flex items-center justify-center gap-1 rounded-full bg-gray-100 p-1! px-2! ring-pink-500 hover:brightness-95 focus-visible:shadow-pink-500 focus-visible:outline-hidden focus-visible:ring-1 xl:px-3! xl:py-1.5! dark:bg-gray-800')} size={{
    default: 'sm',
    lg: 'default',
    xl: 'lg'
  }}>
      <SafelistedChannelsIcon hasSafelistPermission={hasSafelistPermission} overduePaymentId={overduePaymentId} safelistedChannelCount={safelistedChannelCount} />
      {safelistedChannels?.slice(0, 3).map(channel => <ChannelImage channel={channel} className={cn({
      'opacity-50': !!overduePaymentId
    })} key={channel.id} />)}
      {showAddChannelIcon && !isPaused && <div className="flex aspect-square h-full items-center justify-center rounded-full border border-gray-150 border-dashed bg-white dark:bg-gray-700">
          <div className="flex size-4 flex-col items-center justify-center rounded-full bg-pink-500 shadow-md">
            <IconPlus className="size-3/4 text-white" />
          </div>
        </div>}
      {showEllipsis && <Ellipsis className="text-gray-700 dark:text-gray-300" />}
      <span className="sr-only">
        {safelistedChannelCount} of {safelistedChannelLimit} safelisted
      </span>
    </Button>;
});
SafelistedChannels.displayName = 'SafelistedChannels';
export default SafelistedChannels;